import { Injectable } from "@angular/core";
import { TokenRepo } from "@app/core/shared/domain/token.repo";
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  from,
  Observable,
  of,
  shareReplay,
  tap,
  throwError,
} from "rxjs";
import { UserConfigurationRepo } from "@app/core/user/domain/repositories/user-configuration.repo";
import { MissingCredentialsError } from "@app/core/shared/domain/missing-credentials.error";
import { UserConfiguration } from "@app/core/user/domain/user-configuration";

@Injectable({
  providedIn: "root",
})
export class GetUserConfigurationUseCase {
  constructor(
    private tokenRepo: TokenRepo,
    private userConfigurationRepo: UserConfigurationRepo
  ) {}

  private cache$ = new BehaviorSubject<UserConfiguration | null | undefined>(
    undefined
  );
  private inFlight$: Observable<UserConfiguration | null> | null = null;

  execute(): Observable<UserConfiguration | null> {
    const cached = this.cache$.value;
    if (cached !== undefined) {
      return of(cached);
    }
    if (this.inFlight$ !== null) {
      return this.inFlight$;
    }
    this.inFlight$ = from(this.getUserConfiguration()).pipe(
      tap({
        next: (configuration) => {
          this.cache$.next(configuration);
          this.inFlight$ = null;
        },
      }),
      catchError((error) => {
        this.inFlight$ = null;
        return throwError(() => error);
      }),
      shareReplay(1)
    );
    return this.inFlight$;
  }

  setState(configuration: UserConfiguration | null): void {
    this.cache$.next(configuration);
  }

  invalidate(): void {
    this.cache$.next(undefined);
    this.inFlight$ = null;
  }

  private async getUserConfiguration(): Promise<UserConfiguration | null> {
    const token = this.tokenRepo.getTemporal() ?? this.tokenRepo.get();
    if (token == null) {
      throw new MissingCredentialsError();
    }
    return firstValueFrom(this.userConfigurationRepo.get(token));
  }
}
